import { useMutation } from '@tanstack/react-query';
import { Col, DatePicker, Form, Modal, Row, Select } from 'antd';
import { FormInstance, useForm } from 'antd/es/form/Form';
import { PlantApi } from 'apis/PlantApis';
import { SaleTeamApi } from 'apis/SaleTeamApi';
import { SelectForm } from 'components';
import LazySelectForm, {
  ILazySelectData,
  ILazySelectParams,
  initLazySelectData,
} from 'components/Select/LazySelectForm';
import { useUser } from 'contexts/UserProvider';
import dayjs from 'dayjs';
import { IFilterTeamSaleCb } from 'interfaces/TeamSale/TeamSale';
import React, { useEffect, useState } from 'react';
import convertUserTeamSalesToCbb from 'utils/convertUserTeamSalesToCbb';

const optionMonth = [
  { value: '1', label: 'Tháng 1' },
  { value: '2', label: 'Tháng 2' },
  { value: '3', label: 'Tháng 3' },
  { value: '4', label: 'Tháng 4' },
  { value: '5', label: 'Tháng 5' },
  { value: '6', label: 'Tháng 6' },
  { value: '7', label: 'Tháng 7' },
  { value: '8', label: 'Tháng 8' },
  { value: '9', label: 'Tháng 9' },
  { value: '10', label: 'Tháng 10' },
  { value: '11', label: 'Tháng 11' },
  { value: '12', label: 'Tháng 12' },
];

const CreateSalePlanningAuto = ({
  // optionTeamSales,
  defaultFilter,
  open,
  handleOK,
  handleCancel,
}: {
  // optionTeamSales: any;
  defaultFilter?: any;
  open: boolean;
  handleOK: (form: FormInstance<any>) => Promise<void>;
  handleCancel: (form: FormInstance<any>) => void;
}) => {
  const { user } = useUser();
  const [form] = useForm();
  const [disabledCreateBtn, setDisabledCreateBtn] = React.useState(true);
  const [optionFilterMonth, setOptionFilterMonth] = React.useState<any[]>([]);
  const [teamSaleData, setTeamSaleData] =
    useState<ILazySelectData>(initLazySelectData);
  const [optionPlants, setPlants] = useState<any>({
    items: [],
    pageInfo: {},
  });
  const [loading, setLoading] = useState(false);

  function handleChangeForm() {
    const yearValue = parseInt(
      form?.getFieldValue('year')?.format('YYYY') ?? dayjs().year(),
    );
    if (yearValue === dayjs().year()) {
      setOptionFilterMonth(
        optionMonth.filter(
          (item) => parseInt(item.value) >= dayjs().month() + 1,
        ),
      );
      if (parseInt(form.getFieldValue('month')) < dayjs().month() + 1) {
        form.setFieldsValue({ month: undefined });
      }
    } else {
      setOptionFilterMonth(optionMonth);
    }

    setDisabledCreateBtn(
      !form.getFieldValue('teamSalesId')?.length ||
        !form.getFieldValue('month') ||
        !form.getFieldValue('year'),
    );
  }

  const { isPending: getTeamSaleLoading, mutate: getTeamSales } = useMutation({
    mutationFn: (params: IFilterTeamSaleCb) =>
      SaleTeamApi.getAllTeamSaleCbSubSort({
        ...params,
        useYn: true,
        plantId: form.getFieldValue('plantId'),
      }),
    onSuccess: (res) => {
      if (res?.data?.getTeamSales) {
        const items = res.data?.getTeamSales?.items ?? [],
          totalItems = res.data?.getTeamSales?.pageInfo?.totalItems ?? 0;

        setTeamSaleData({
          options: items
            .filter((item: any) => item?.subId)
            .map((item: any) => ({
              label: `${item?.subId} - ${item?.name}`,
              value: item?.id,
            })),
          totalItems: totalItems,
          hasNextPage: res.data?.getTeamSales?.pageInfo?.hasNextPage,
        });
      }
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const { mutateAsync: getAllPlant, isPending: getAllPlantPending } =
    useMutation({
      mutationFn: () => PlantApi.getAllPlantComboBox(),
      onSuccess: (data: any) => {
        const items = data.data?.getPlant?.items || [];

        if (!items?.length) return;
        setPlants(() => ({
          items: items.map((item: any) => ({
            label: item.id,
            value: item.id,
          })),
          pageInfo: data.data.getPlant.pageInfo,
        }));
      },
      onError: (error) => {
        console.log(error);
      },
    });

  const handleTeamSaleScrollChange = (params: ILazySelectParams) => {
    const plantId = form.getFieldValue('plantId');

    if (!plantId) return;

    const teamSaleParams: IFilterTeamSaleCb = {
      ...params,
    };

    getTeamSales(teamSaleParams);
  };

  const handleChangePlant = () => {
    form.setFieldsValue({ teamSalesId: undefined });

    setDisabledCreateBtn(true);
    handleTeamSaleScrollChange({
      pageSize: 20,
    });
  };

  // useEffect(() => {
  //   mutationGetAllTeamSale.mutate();
  // }, [user]);

  const handleCreateAutoSalePlan = async () => {
    setLoading(true);

    try {
      await handleOK(form);
    } catch (error) {
      //
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleChangeForm();
    form.setFieldsValue({ plantId: defaultFilter?.plantId });

    if (open && (user?.isAdmin || user?.isHO)) {
      setTeamSaleData(initLazySelectData);
    }
  }, [open]);

  useEffect(() => {
    getAllPlant();

    if (defaultFilter.plantId) {
      form.setFieldsValue({ plantId: defaultFilter.plantId });
    }
  }, []);

  return (
    <Form name="basic" form={form} className="w-full pt-5" layout="vertical">
      <Modal
        open={open}
        title="Tạo kế hoạch bán hàng tự động"
        onOk={handleCreateAutoSalePlan}
        onCancel={() => handleCancel(form)}
        okText="Tạo kế hoạch"
        cancelText="Hủy"
        okButtonProps={{ disabled: disabledCreateBtn }}
        width={600}
        confirmLoading={loading}
      >
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              labelCol={{ span: 24 }}
              label="Chi nhánh"
              name="plantId"
              rules={[
                {
                  required: true,
                  message: 'Vui lòng chọn đội bán hàng',
                },
              ]}
              required
            >
              <SelectForm
                disabled={!(user?.isAdmin || user?.isHO)}
                className="text-red"
                loading={getAllPlantPending}
                placeholder="Chọn chi nhánh"
                options={optionPlants.items}
                fieldNames={{
                  label: 'value',
                }}
                allowClear={false}
                onChange={handleChangePlant}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              labelCol={{ span: 24 }}
              name={'teamSalesId'}
              label="Đội bán hàng"
              rules={[
                {
                  required: true,
                  message: 'Vui lòng chọn đội bán hàng',
                },
              ]}
              required
            >
              {user?.isSale ? (
                <SelectForm
                  mode="multiple"
                  placeholder="Chọn đội"
                  options={convertUserTeamSalesToCbb(user)}
                  onChange={handleChangeForm}
                />
              ) : (
                <LazySelectForm
                  hasNextPage={teamSaleData.hasNextPage}
                  // disabled={!teamSaleData.options.length}
                  mode="multiple"
                  customeSize={20}
                  data={teamSaleData}
                  placeholder="Chọn đội"
                  loading={getTeamSaleLoading}
                  onSelectScrollChange={handleTeamSaleScrollChange}
                  onChange={handleChangeForm}
                />
              )}
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              labelCol={{ span: 24 }}
              name={'year'}
              label="Năm"
              className="flex-1"
              initialValue={dayjs()}
              rules={[
                {
                  required: true,
                  message: 'Vui lòng chọn năm',
                },
              ]}
              required
            >
              <DatePicker
                rootClassName="w-full"
                picker="year"
                size="large"
                className="w-full"
                onChange={handleChangeForm}
                allowClear={false}
                minDate={dayjs()}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              labelCol={{ span: 24 }}
              name={'month'}
              label="Tháng"
              className="flex-1"
              rules={[
                {
                  required: true,
                  message: 'Vui lòng chọn tháng',
                },
              ]}
              required
            >
              <Select
                placeholder="Nhập tháng"
                options={optionFilterMonth}
                size="large"
                onChange={handleChangeForm}
              />
            </Form.Item>
          </Col>
        </Row>
        {/* <Flex gap={24}>
          <Form.Item
            name={'year'}
            label="Năm"
            className="flex-1"
            initialValue={dayjs()}
            rules={[
              {
                required: true,
                message: 'Vui lòng chọn năm',
              },
            ]}
            required
          >
            <DatePicker
              rootClassName="w-full"
              picker="year"
              size="large"
              className="w-full"
              onChange={handleChangeForm}
              allowClear={false}
              minDate={dayjs()}
            />
          </Form.Item>
          <Form.Item
            name={'month'}
            label="Tháng"
            className="flex-1"
            rules={[
              {
                required: true,
                message: 'Vui lòng chọn tháng',
              },
            ]}
            required
          >
            <Select
              loading={mutationGetAllTeamSale.isPending}
              placeholder="Nhập tháng"
              options={optionFilterMonth}
              size="large"
              onChange={handleChangeForm}
            />
          </Form.Item>
        </Flex> */}
      </Modal>
    </Form>
  );
};

export default CreateSalePlanningAuto;
